import type { PropsWithChildren } from 'react'
import { useCallback, useContext } from 'react'
import { useIntl } from 'react-intl'

import TextButton from 'LuxKit/Button/TextButton'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import AccountAccessModal from 'components/Account/AccountAccess/AccountAccessModal'
import LoadingAnimation from 'components/Common/Loading/LoadingAnimation'
import WalledContent from 'components/Common/WalledContent'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Group from 'components/utils/Group'
import { OFFER_TYPE_ALWAYS_ON } from 'constants/offer'
import ModalContext from 'contexts/ModalContext'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'

import AccommodationAmenitiesAndPoliciesModal from './AccommodationAmenitiesAndPoliciesModal'
import AccommodationRoomTileMessage from './AccommodationRoomTileMessage'
import styled from 'styled-components'

const Root = styled(Group)`
  flex: 1;
`

export type AccommodationRoomTileActionsState =
  'loading' |
  'sold-out-on-dates' |
  'sold-out' |
  'exceeded-capacity' |
  'sold-out-on-dates-or-exceeded-capacity' |
  'available-on-dates' |
  'available-on-dates-variant' |
  'available-for-some-rooms'

interface Props {
  roomPackage: App.Property.Room | App.HotelPackage | App.BedbankPackage
  state?: AccommodationRoomTileActionsState
  isWalled?: boolean
  offer: App.HotelOffer | App.BedbankOffer
  shouldShowAmenitiesAndPoliciesCta?: boolean
  onChangeOccupancyClick?: () => void
  onChangeDatesClick?: () => void
  onCheckAvailabilityClick?: () => void
  onMultiRoomModeClick?: () => void
}

const errorStates: Array<AccommodationRoomTileActionsState> = [
  'sold-out',
  'sold-out-on-dates',
  'exceeded-capacity',
  'sold-out-on-dates-or-exceeded-capacity',
  'available-for-some-rooms',
]

function AccommodationRoomTileActions(props: PropsWithChildren<Props>) {
  const {
    children,
    roomPackage,
    state,
    offer,
    isWalled,
    shouldShowAmenitiesAndPoliciesCta = true,
    onChangeOccupancyClick,
    onCheckAvailabilityClick,
    onChangeDatesClick,
    onMultiRoomModeClick,
  } = props

  const intl = useIntl()

  const showModal = useContext(ModalContext)

  const openAmenitiesAndPoliciesModal = useCallback(() => {
    showModal(<AccommodationAmenitiesAndPoliciesModal roomPackage={roomPackage} />)
  }, [roomPackage, showModal])

  const onSignUpClick = useCallback(() => {
    showModal(<AccountAccessModal />)
  }, [showModal])

  const isErrorState = !!state && errorStates.includes(state)

  const bestPriceHintText = intl.formatMessage({
    id: 'll1aVmDBIUtPwecj69nx',
    defaultMessage: 'Select dates to get the best price for this offer',
    description: 'Hint text shown to encourage users to pick dates before checking availability.',
  })

  const viewAmenitiesAndPoliciesText = intl.formatMessage({
    id: 'h4TWgeilJiydfqqfht87',
    defaultMessage: 'View amenities & policies',
    description: 'CTA button text to open the amenities and policies modal.',
  })

  const signUpForFreeText = intl.formatMessage({
    id: 'ifUaOBVE8-RKisUoa8DD',
    defaultMessage: 'Sign up for free',
    description: 'CTA button text prompting the user to sign up (walled content).',
  })

  const checkAvailabilityText = intl.formatMessage({
    id: 'rF1Fqk7Ka-NnA2MnYva_',
    defaultMessage: 'Check availability',
    description: 'CTA button text to check room/package availability.',
  })

  const selectDatesText = intl.formatMessage({
    id: 'RkyCUVscJAILoveCQYfQ',
    defaultMessage: 'Select dates',
    description: 'CTA button text to prompt the user to select dates.',
  })

  return <Root direction="vertical" tabletHorizontalAlign="end" tabletVerticalAlign="end" gap={12}>
    {state === 'available-on-dates-variant' && onCheckAvailabilityClick && <CSSBreakpoint min="tablet">
      <BodyTextBlock
        variant="medium"
        weight="bold"
        startIcon={<LineInfoCircleIcon />}
      >
        {bestPriceHintText}
      </BodyTextBlock>
    </CSSBreakpoint>}
    <Group direction="vertical" tabletHorizontalAlign="end" desktopDirection="horizontal" desktopHorizontalAlign="space-between" gap={12}>
      <div>
        <WalledContent
          enforced={isWalled ?? offer.walledGarden}
          wall={<AccommodationRoomTileMessage message="members-exclusive" />}
        >
          {isErrorState && <AccommodationRoomTileMessage
            message={state}
            onMultiRoomModeClick={onMultiRoomModeClick}
            onChangeOccupancyClick={onChangeOccupancyClick}
            onChangeDatesClick={onChangeDatesClick}
          />}
        </WalledContent>
      </div>
      <Group
        direction="vertical"
        verticalAlign="center"
        desktopDirection="horizontal"
        desktopHorizontalAlign="end"
        gap={8}
        wrap="wrap"
        noDisplayWhenEmpty
      >
        {shouldShowAmenitiesAndPoliciesCta && <CSSBreakpoint min="desktop">
          <TextButton size="large" kind="tertiary" onClick={openAmenitiesAndPoliciesModal}>
            {viewAmenitiesAndPoliciesText}
          </TextButton>
        </CSSBreakpoint>}
        <WalledContent
          enforced={isWalled ?? offer.walledGarden}
          wall={offer.type !== OFFER_TYPE_ALWAYS_ON && <TextButton size="large" kind="secondary" onClick={onSignUpClick}>
            {signUpForFreeText}
          </TextButton>}
        >
          {children}
          {state === 'available-on-dates' && onCheckAvailabilityClick && <TextButton
            size="large"
            data-testid="room-tile-action-check-availability"
            kind="primary"
            onClick={onCheckAvailabilityClick}
          >
            {checkAvailabilityText}
          </TextButton>}
          {state === 'available-on-dates-variant' && onCheckAvailabilityClick && <>
            <CSSBreakpoint max="mobile">
              <BodyTextBlock
                variant="medium"
                weight="bold"
                startIcon={<LineInfoCircleIcon />}
              >
                {bestPriceHintText}
              </BodyTextBlock>
            </CSSBreakpoint>
            <TextButton
              size="large"
              data-testid="room-tile-action-check-availability"
              kind="primary"
              onClick={onCheckAvailabilityClick}
            >
              {selectDatesText}
            </TextButton>
          </>}
        </WalledContent>
        {state === 'loading' && <LoadingAnimation size={12} />}
      </Group>
    </Group>
  </Root>
}

export default withOfferPageAndReduxState((offerPageState) => ({
  offer: (offerPageState.offer ?? offerPageState.bedbankOffer)!,
}), AccommodationRoomTileActions)
